import React from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmDialog } from '@/components/deleteConfirmDialog';
import { ErrorBoundary } from '@/components/errorBoundary';
import { CategoryAttributesSheet, CategoryFormModal } from './components';
import { CategoriesPageSkeleton } from './CategoriesPageSkeleton';
import { CategoriesPageView } from './CategoriesPageView';
import { useCategoriesPage } from './useCategoriesPage';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * /categories page — the catalogue's category management view. Owns its data
 * hook (`useCategoriesPage`) and an `ErrorBoundary`; the throw lives in the
 * inner Content child because React boundaries don't catch errors thrown by the
 * component that renders them. The form modal and attributes drawer render
 * outside the boundary so a grid render failure can't take them down.
 */
export const CategoriesPage: React.FC = () => {
    const page = useCategoriesPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('categories.components.categoryTable.errorTitle'),
            errorMessage: t('categories.components.categoryTable.errorMessage'),
            errorRetry: t('categories.components.categoryTable.errorRetry'),
        }),
        [t],
    );

    return (
        <div
            className="mx-auto flex w-full max-w-7xl flex-col gap-8"
            {...getNodeTestIdProp('categories-page')}
            id="categories-page"
        >
            <ErrorBoundary
                data-testid="categories-page-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="categories-page-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <CategoriesPageContent page={page} />
            </ErrorBoundary>
            <DeleteConfirmDialog
                confirm={page.deleteConfirm}
                descriptionKey="deleteConfirm.categoryDescription"
                promptKey="deleteConfirm.categoryPrompt"
            />
            <CategoryFormModal
                error={page.formError}
                initialValues={page.editingInitialValues}
                isOpen={page.isFormOpen}
                isSubmitting={page.isSubmitting}
                mode={page.formMode}
                onClose={page.onCloseForm}
                onSubmit={page.onSubmitCategory}
                parentOptions={page.parentOptions}
            />
            <CategoryAttributesSheet
                categoryId={page.attributesCategoryId}
                categoryName={page.attributesCategoryName}
                categorySlug={page.attributesCategorySlug}
                isOpen={page.isAttributesOpen}
                onClose={page.onCloseAttributes}
            />
        </div>
    );
};

type CategoriesPageContentProps = {
    page: ReturnType<typeof useCategoriesPage>;
};

const CategoriesPageContent: React.FC<CategoriesPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    if (page.isLoading || page.data === null) {
        return <CategoriesPageSkeleton />;
    }

    return (
        <CategoriesPageView
            data={page.data}
            expandedKeys={page.expandedKeys}
            onAddCategory={page.onAddCategory}
            onDeleteCategory={page.onDeleteCategory}
            onEditCategory={page.onEditCategory}
            onExpandedChange={page.onExpandedChange}
            onManageAttributes={page.onManageAttributes}
            onPageChange={page.onPageChange}
            onPageSizeChange={page.onPageSizeChange}
            onSearchChange={page.onSearchChange}
            page={page.page}
            pageSize={page.pageSize}
            pageWindow={page.pageWindow}
            search={page.search}
            totalCount={page.totalCount}
            totalPages={page.totalPages}
        />
    );
};
